import {Tabs} from 'antd'
import {useNavigate, useLocation, useParams, Outlet, Navigate} from 'react-router-dom'
import profileRouteConstants from '@constants/profile.routeConstants'
import patientProfilePaths from '@staticData/patientProfile.paths'
import {useFeatureAccess} from '@hooks/useFeatureAccess'
import {withKanbanParam} from 'utils/ConstFunctions'
import useProfileBasePath from '@hooks/useProfileBasePath'

const {TabPane} = Tabs

const ActivityLog = () => {
  const navigate = useNavigate()
  const profileBasePath = useProfileBasePath()
  const location = useLocation()
  const {patientId} = useParams()
  const {permissionChecks} = useFeatureAccess()
  const activityLogsTabAccess = permissionChecks?.patientProfileActions
  // /profile/:patientId/activity-logs/:child
  const [, , , , child] = location.pathname.split('/')
  
  const params = new URLSearchParams(window.location.search)
  const kanbanName = params.get('kanban_name')
  const orderId = params.get('order_id') // Get order_id from URL
  const treatmentPlanId = params.get('treatment_plan_id') // Get treatment_plan_id from URL

  const activityRoute = patientProfilePaths.find(
    (route) => route.value === profileRouteConstants.ACTIVITY_LOGS
  )
  const childRoutes = activityRoute?.children || []

  // Helper function to build URL with preserved query parameters (same as Details.tsx)
  const buildUrlWithParams = (path: string) => {
    let url = `${profileBasePath}/${patientId}/activity-logs/${path}`
    
    // Get current URL parameters
    const currentParams = new URLSearchParams(window.location.search)
    
    // Parameters to preserve
    const paramsToPreserve = ['kanban_name', 'order_id', 'treatment_plan_id']
    let hasParams = false
    
    paramsToPreserve.forEach(param => {
      const value = currentParams.get(param)
      if (value) {
        const urlObj = new URL(url, window.location.origin)
        urlObj.searchParams.set(param, value)
        url = `${urlObj.pathname}${urlObj.search}`
        hasParams = true
      }
    })
    
    // Handle kanban_name separately if it was passed via withKanbanParam
    if (kanbanName && !hasParams) {
      url = withKanbanParam(url, kanbanName)
    } else if (kanbanName) {
      // If we already have params but kanban_name wasn't included, add it
      const urlObj = new URL(url, window.location.origin)
      if (!urlObj.searchParams.has('kanban_name')) {
        urlObj.searchParams.set('kanban_name', kanbanName)
        url = `${urlObj.pathname}${urlObj.search}`
      }
    }
    
    return url
  }

  // Determine which child routes are viewable based on permissions
  const isRouteViewable = (path: string | undefined) => {
    if (!path) return false
    if (path === 'notes') return activityLogsTabAccess?.internalNotesTab?.isViewable !== false
    if (path === 'audit-logs') return activityLogsTabAccess?.auditLogsTab?.isViewable !== false
    if (path === 'comments') return activityLogsTabAccess?.commentsTab?.isViewable !== false
    return true
  }

  const visibleChildRoutes = childRoutes.filter((r) => isRouteViewable(r.path))

  // If no child in URL, redirect to the first *visible* child
  if (!child && visibleChildRoutes.length > 0) {
    return (
      <Navigate
        to={buildUrlWithParams(visibleChildRoutes[0].path)}
        replace
      />
    )
  }

  // If the current child in URL is not viewable, redirect to the first visible child
  if (child && !isRouteViewable(child) && visibleChildRoutes.length > 0) {
    return (
      <Navigate
        to={buildUrlWithParams(visibleChildRoutes[0].path)}
        replace
      />
    )
  }

  return (
    <div className='h-full flex flex-col'>
      <Tabs
        className='h-full flex flex-col prescription-tabs'
        activeKey={child || childRoutes[0]?.path}
        onChange={(key) => {
          navigate(buildUrlWithParams(key))
        }}
      >
        {visibleChildRoutes.map((route) => {
          return (
            <TabPane tab={route.label} key={route.path} className='h-full'>
              {child === route.path && <Outlet />}
            </TabPane>
          )
        })}
      </Tabs>
    </div>
  )
}

export default ActivityLog